import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase, Profile } from '../lib/supabase';
import './OddsCalculator.css';
import hbsLogo from '../assets/HBS_LOGO.png';
import stanfordLogo from '../assets/STANFORD_LOGO.png';
import whartonLogo from '../assets/WHARTON_LOGO.png';
import columbiaLogo from '../assets/COLUMBIA_LOGO.png';
import mitLogo from '../assets/MIT_LOGO.png';
import chicagoBoothLogo from '../assets/CHICAGO_BOOTH_LOGO.png';
import kelloggLogo from '../assets/KELLOGG_LOGO.png';
import berkeleyHaasLogo from '../assets/BERKELEY_HAAS_LOGO.png';
import yaleSomLogo from '../assets/YALE_SOM_LOGO.png';
import dardenLogo from '../assets/DARDEN_LOGO.png';
import uclaAndersonLogo from '../assets/UCLA_ANDERSON_LOGO.png';
import emoryGoizuetaLogo from '../assets/EMORY_GOIZUETA_LOGO.png';

type OddsTier = 'Reach' | 'Target' | 'Likely';
type Confidence = 'Low' | 'Medium' | 'High';

type SchoolConfig = {
  name: string;
  logo: string;
  gpaMedian: number;
  gmatMedian: number;
  greMedian: number;
  /** Approximate overall admit-rate anchor (not deferred-specific official data). */
  baseRate: number;
  overview: string;
  statsUrl: string;
  statsAsOf: string;
};

type OddsResult = {
  low: number;
  high: number;
  tier: OddsTier;
  confidence: Confidence;
  strengths: string[];
  risks: string[];
  actions: string[];
};

const ANALYSIS_LOADING_MESSAGES = [
  'Reviewing your academic profile...',
  'Comparing against recent class benchmarks...',
  'Weighing leadership and story signals...',
  'Building your school-specific odds range...'
] as const;

const SCHOOL_CONFIGS: SchoolConfig[] = [
  {
    name: 'Harvard Business School',
    logo: hbsLogo,
    gpaMedian: 3.7,
    gmatMedian: 740,
    greMedian: 326,
    baseRate: 0.11,
    overview: 'Case-method MBA with high leadership expectations and a globally diverse class profile.',
    statsUrl: 'https://www.hbs.edu/mba/admissions/class-profile/Pages/default.aspx',
    statsAsOf: '2026'
  },
  {
    name: 'Stanford Graduate School of Business',
    logo: stanfordLogo,
    gpaMedian: 3.8,
    gmatMedian: 738,
    greMedian: 327,
    baseRate: 0.08,
    overview: 'Small, highly selective program known for innovation, entrepreneurship, and personal leadership fit.',
    statsUrl: 'https://www.gsb.stanford.edu/programs/mba/admission/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'Wharton School',
    logo: whartonLogo,
    gpaMedian: 3.7,
    gmatMedian: 732,
    greMedian: 325,
    baseRate: 0.23,
    overview: 'Analytical, finance-strong MBA with broad leadership pathways and extensive curricular flexibility.',
    statsUrl: 'https://mba.wharton.upenn.edu/class-profile/',
    statsAsOf: '2026'
  },
  {
    name: 'Columbia Business School',
    logo: columbiaLogo,
    gpaMedian: 3.7,
    gmatMedian: 727,
    greMedian: 323,
    baseRate: 0.22,
    overview: 'NYC-based MBA with strong access to finance, media, and in-semester industry opportunities.',
    statsUrl: 'https://academics.gsb.columbia.edu/mba/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'MIT Sloan',
    logo: mitLogo,
    gpaMedian: 3.7,
    gmatMedian: 730,
    greMedian: 325,
    baseRate: 0.20,
    overview: 'Innovation-focused MBA emphasizing data-driven leadership, action learning, and technology ecosystems.',
    statsUrl: 'https://mitsloan.mit.edu/mba/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'Chicago Booth',
    logo: chicagoBoothLogo,
    gpaMedian: 3.6,
    gmatMedian: 729,
    greMedian: 324,
    baseRate: 0.22,
    overview: 'Flexible curriculum with rigorous analytics and a strong reputation for intellectual curiosity.',
    statsUrl: 'https://www.chicagobooth.edu/mba/full-time/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'Kellogg SOM',
    logo: kelloggLogo,
    gpaMedian: 3.7,
    gmatMedian: 733,
    greMedian: 325,
    baseRate: 0.23,
    overview: 'Team-oriented leadership culture with strengths in marketing, strategy, and collaborative impact.',
    statsUrl: 'https://www.kellogg.northwestern.edu/programs/full-time-mba/admissions/class-profile.aspx',
    statsAsOf: '2026'
  },
  {
    name: 'Berkeley Haas',
    logo: berkeleyHaasLogo,
    gpaMedian: 3.7,
    gmatMedian: 718,
    greMedian: 321,
    baseRate: 0.20,
    overview: 'Values-driven MBA tied to the Bay Area innovation ecosystem and principled leadership model.',
    statsUrl: 'https://mba.haas.berkeley.edu/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'Yale SOM',
    logo: yaleSomLogo,
    gpaMedian: 3.7,
    gmatMedian: 730,
    greMedian: 325,
    baseRate: 0.20,
    overview: 'Mission-driven MBA with cross-sector focus and strong integration with broader Yale resources.',
    statsUrl: 'https://som.yale.edu/programs/mba/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'Darden School of Business',
    logo: dardenLogo,
    gpaMedian: 3.6,
    gmatMedian: 715,
    greMedian: 320,
    baseRate: 0.26,
    overview: 'Case-method, general-management oriented MBA emphasizing communication and leadership execution.',
    statsUrl: 'https://www.darden.virginia.edu/mba/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'UCLA Anderson School of Management',
    logo: uclaAndersonLogo,
    gpaMedian: 3.6,
    gmatMedian: 714,
    greMedian: 320,
    baseRate: 0.30,
    overview: 'West Coast MBA with strengths in tech, entertainment, and entrepreneurial career pathways.',
    statsUrl: 'https://www.anderson.ucla.edu/degrees/full-time-mba/admissions/class-profile',
    statsAsOf: '2026'
  },
  {
    name: 'Emory Goizueta Business School',
    logo: emoryGoizuetaLogo,
    gpaMedian: 3.5,
    gmatMedian: 707,
    greMedian: 317,
    baseRate: 0.32,
    overview: 'Small-cohort MBA with close faculty access, leadership development, and strong recruiting outcomes.',
    statsUrl: 'https://goizueta.emory.edu/full-time-mba/admissions/class-profile',
    statsAsOf: '2026'
  }
];

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

function estimateOdds(profile: Profile, school: SchoolConfig): OddsResult {
  // Start from each school's baseline and apply modest profile-based deltas.
  let centerPct = school.baseRate * 100;
  const strengths: string[] = [];
  const risks: string[] = [];
  const actions: string[] = [];
  let knownSignals = 0;

  if (typeof profile.gpa === 'number') {
    knownSignals += 1;
    const delta = profile.gpa - school.gpaMedian;
    centerPct += clamp(delta * 10, -7, 7);
    if (delta >= 0.15) strengths.push(`GPA is above ${school.name}'s typical range.`);
    if (delta <= -0.2) risks.push('GPA is below the school median for recent admits.');
  } else {
    risks.push('No GPA on file yet.');
    actions.push('Add your GPA and scale in Profile Builder for a sharper estimate.');
  }

  const hasGmat = typeof profile.gmat_score === 'number';
  const hasGre = typeof profile.gre_score === 'number';
  if (hasGmat || hasGre) {
    knownSignals += 1;
    if (hasGmat) {
      const delta = (profile.gmat_score as number) - school.gmatMedian;
      centerPct += clamp(delta / 8, -6, 6);
      if (delta >= 20) strengths.push('GMAT is competitive for this school.');
      if (delta <= -20) risks.push('GMAT is below the school median.');
    } else if (hasGre) {
      const delta = (profile.gre_score as number) - school.greMedian;
      centerPct += clamp((delta / 2.5) * 1.2, -6, 6);
      if (delta >= 4) strengths.push('GRE is competitive for this school.');
      if (delta <= -4) risks.push('GRE is below the school median.');
    }
  } else {
    centerPct -= 6;
    risks.push('No GMAT or GRE score on file.');
    actions.push('A stronger test score can materially improve your odds.');
  }

  const work = (profile.work_experience || '').trim();
  if (work.length > 0) {
    knownSignals += 1;
    if (work.length > 180) {
      centerPct += 2.5;
      strengths.push('Work experience context is detailed.');
    } else {
      centerPct += 0.8;
      risks.push('Work experience detail is limited.');
    }
  } else {
    centerPct -= 2;
    risks.push('No work experience context added.');
    actions.push('Add internships/projects with quantified impact.');
  }

  const extracurriculars = (profile.extracurriculars || '').trim();
  if (extracurriculars.length > 0) {
    knownSignals += 1;
    if (/president|founder|lead|captain|director|chair/i.test(extracurriculars)) {
      centerPct += 2.5;
      strengths.push('Leadership signals in extracurriculars are strong.');
    } else {
      centerPct += 1;
      risks.push('Leadership ownership is not explicit in extracurriculars.');
    }
  } else {
    centerPct -= 2;
    risks.push('No extracurricular leadership context provided.');
    actions.push('Add leadership-heavy activities and outcomes.');
  }

  const goalsText = `${profile.desired_outcomes || ''} ${profile.why_mba || ''}`.trim();
  if (goalsText.length > 0) {
    knownSignals += 1;
    if (goalsText.length > 180) {
      centerPct += 3;
      strengths.push('Career goals/why MBA rationale appears developed.');
    } else {
      centerPct += 1;
      risks.push('Career goals rationale may be too brief.');
    }
  } else {
    centerPct -= 3;
    risks.push('Career goals / why MBA is missing.');
    actions.push('Clarify goals and why this school helps close the gap.');
  }

  if ((profile.target_schools || []).includes(school.name)) {
    centerPct += 0.8;
  } else {
    actions.push('Add this school to your target list to tailor your narrative.');
  }

  centerPct = clamp(centerPct, 2, 75);
  const spread = knownSignals >= 5 ? 6 : knownSignals >= 3 ? 9 : 12;
  const low = Math.round(clamp(centerPct - spread, 1, 90));
  const high = Math.round(clamp(centerPct + spread, 3, 95));

  let tier: OddsTier = 'Reach';
  if (low >= 30) tier = 'Likely';
  else if (high >= 22) tier = 'Target';

  const confidence: Confidence =
    knownSignals >= 5 ? 'High' : knownSignals >= 3 ? 'Medium' : 'Low';

  const defaultAction = school.gmatMedian > 725
    ? 'Push your test score toward this school’s median and add measurable impact examples.'
    : 'Tighten your story fit and quantify results in your internships/projects.';

  return {
    low,
    high,
    tier,
    confidence,
    strengths: strengths.slice(0, 3),
    risks: risks.slice(0, 3),
    actions: [...actions.slice(0, 2), defaultAction].slice(0, 3)
  };
}

const OddsCalculator: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<string>(SCHOOL_CONFIGS[0].name);
  const [oddsResult, setOddsResult] = useState<OddsResult | null>(null);
  const [oddsCalculatedAt, setOddsCalculatedAt] = useState<Date | null>(null);
  const [isCalculatingOdds, setIsCalculatingOdds] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (profileError) throw profileError;
      setProfile((data as Profile | null) ?? null);
      if (!data) {
        setError('No profile found yet. Save your profile first in Profile Builder.');
      }
    } catch (e) {
      console.error(e);
      setError('Could not load your profile from Supabase.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (authLoading) return;
    void loadProfile();
  }, [authLoading, loadProfile]);

  const school = useMemo(
    () => SCHOOL_CONFIGS.find((s) => s.name === selectedSchool) ?? SCHOOL_CONFIGS[0],
    [selectedSchool]
  );

  useEffect(() => {
    // Require a deliberate user action each time school/profile context changes.
    setOddsResult(null);
    setOddsCalculatedAt(null);
  }, [selectedSchool, profile]);

  useEffect(() => {
    if (!isCalculatingOdds) {
      setLoadingMessageIndex(0);
      return;
    }
    const intervalId = window.setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % ANALYSIS_LOADING_MESSAGES.length);
    }, 850);
    return () => window.clearInterval(intervalId);
  }, [isCalculatingOdds]);

  const handleCalculateOdds = useCallback(async () => {
    if (!profile) return;
    setIsCalculatingOdds(true);
    try {
      const calculated = estimateOdds(profile, school);
      const minimumDelayMs = 1700;
      await new Promise((resolve) => window.setTimeout(resolve, minimumDelayMs));
      setOddsResult(calculated);
      setOddsCalculatedAt(new Date());
    } finally {
      setIsCalculatingOdds(false);
    }
  }, [profile, school]);

  if (authLoading || isLoading) {
    return (
      <div className="odds-page">
        <div className="odds-container">
          <p className="odds-loading">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="odds-page">
      <div className="odds-container">
        <header className="odds-header">
          <h1 className="odds-title">Admissions Odds Calculator</h1>
          <p className="odds-subtitle">
            Select one school at a time. Odds are a holistic estimate shown as a range (not a guaranteed probability).
          </p>
        </header>

        {!user && (
          <div className="odds-auth-callout">
            <p>
              <strong>Sign in</strong> to calculate odds from your saved profile.
            </p>
            <p>
              <Link to="/login">Log in</Link> or <Link to="/signup">create an account</Link>.
            </p>
          </div>
        )}

        {error && <div className="odds-error">{error}</div>}

        {!profile && user && (
          <div className="odds-empty">
            Build and save your profile first in <Link to="/profile-builder">Profile Builder</Link>.
          </div>
        )}

        {profile && (
          <>
            <section className="odds-school-picker" aria-label="Select one school">
              {SCHOOL_CONFIGS.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  className={`odds-school-button ${selectedSchool === s.name ? 'active' : ''}`}
                  onClick={() => setSelectedSchool(s.name)}
                  title={s.name}
                >
                  <img src={s.logo} alt="" className="odds-school-logo" />
                </button>
              ))}
            </section>

            <section className="odds-result-card">
              <div className="odds-result-top">
                <h2>{school.name}</h2>
              </div>

              <section className="odds-school-overview" aria-label={`Overview for ${school.name}`}>
                <p className="odds-school-overview-text">{school.overview}</p>
                <div className="odds-school-overview-stats">
                  <span><strong>Median GPA:</strong> {school.gpaMedian}</span>
                  <span><strong>Median GMAT:</strong> {school.gmatMedian}</span>
                  <span><strong>Median GRE:</strong> {school.greMedian}</span>
                </div>
                <a
                  className="odds-school-stats-link"
                  href={school.statsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  View official admissions statistics
                </a>
                <p className="odds-school-stats-asof">Stats source last reviewed: {school.statsAsOf}</p>
              </section>

              <div className="odds-profile-snaps">
                <div><span>Your GPA</span><strong>{typeof profile.gpa === 'number' ? profile.gpa : '—'}</strong></div>
                <div><span>Your GMAT</span><strong>{typeof profile.gmat_score === 'number' ? profile.gmat_score : '—'}</strong></div>
                <div><span>Your GRE</span><strong>{typeof profile.gre_score === 'number' ? profile.gre_score : '—'}</strong></div>
              </div>

              <div className="odds-calc-action">
                <div className="odds-calc-buttons">
                  <button
                    type="button"
                    className="odds-calc-button"
                    onClick={() => void handleCalculateOdds()}
                    disabled={isCalculatingOdds}
                  >
                    {isCalculatingOdds ? 'Calculating...' : oddsResult ? 'Recalculate Odds' : 'Calculate Odds'}
                  </button>
                  <Link to="/profile-builder" className="odds-edit-profile-link">
                    Edit Profile
                  </Link>
                </div>
                {!oddsResult && !isCalculatingOdds && (
                  <p className="odds-calc-hint">Select a school and click calculate for a fresh analysis.</p>
                )}
              </div>

              {isCalculatingOdds && (
                <div className="odds-loading-analysis" role="status" aria-live="polite">
                  <div className="odds-loader" aria-hidden="true">
                    <span className="odds-loader-ring odds-loader-ring-one" />
                    <span className="odds-loader-ring odds-loader-ring-two" />
                    <span className="odds-loader-core" />
                  </div>
                  <p>{ANALYSIS_LOADING_MESSAGES[loadingMessageIndex]}</p>
                </div>
              )}

              {oddsResult && (
                <section className={`odds-hero-result odds-hero-result-${oddsResult.tier.toLowerCase()}`} aria-label="Estimated odds result">
                  <p className="odds-hero-label">Your Estimated Odds</p>
                  <div className="odds-hero-main">
                    <div className="odds-hero-range">
                      {oddsResult.low}% - {oddsResult.high}%
                    </div>
                    <div className="odds-hero-meta">
                      <span className="odds-hero-tier">{oddsResult.tier}</span>
                      <span className="odds-hero-divider">•</span>
                    <span>{oddsResult.confidence} confidence</span>
                    </div>
                  </div>
                  <p className="odds-hero-time">
                    {oddsCalculatedAt ? `Calculated at ${oddsCalculatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Calculated just now'}
                  </p>
                </section>
              )}

              {oddsResult && (
                <div className="odds-columns">
                  <div className="odds-col">
                    <h3>Strength Drivers</h3>
                    <ul>{oddsResult.strengths.map((x) => <li key={x}>{x}</li>)}</ul>
                  </div>
                  <div className="odds-col">
                    <h3>Risk Drivers</h3>
                    <ul>{oddsResult.risks.map((x) => <li key={x}>{x}</li>)}</ul>
                  </div>
                  <div className="odds-col">
                    <h3>How to Improve</h3>
                    <ul>{oddsResult.actions.map((x) => <li key={x}>{x}</li>)}</ul>
                  </div>
                </div>
              )}
              {oddsResult && (
                <p className="odds-disclaimer">
                  This estimate is directional only. MBA admissions decisions are holistic and can change based on essays,
                  recommendations, interview performance, school priorities, and round dynamics.
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default OddsCalculator;

